import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { get } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';
import { ArrowLeftIcon } from 'lucide-react';
import { useForm, useWatch, FormProvider, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { IntegrationIcon } from '@/components/organisms/Integrations';
import { PageSubHeader } from '@/components/organisms/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import useBrains from '@/hooks/useBrains';
import { selectAccountSlug, selectCampaignId } from '@/redux/session/selectors';
import { VoiceCallSettings } from '@/shared/types/campaigns';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelDisplayName } from '@/util/channelName';
import { createYupResolver } from '@/util/forms/createYupResolver';

import { IntegrationSelectContent } from '../../components/BuildSteps/IntegrationSelectContent';
import { MessageAuthoring } from '../../components/BuildSteps/MessageAuthoring';
import { MessagePreview } from '../../components/BuildSteps/MessagePreview';
import { useCampaignBuildLogic } from '../../components/BuildSteps/useCampaignsBuildForm';
import { CampaignBreadcrumbs } from '../../components/CampaignBreadcrumbs';
import { BuildPageLoadingState } from '../../components/CampaignLoadingState';
import { CampaignWrapper } from '../../components/CampaignWrapper';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useCampaignSenders } from '../../hooks/useCampaignSenders';
import {
  DraftCampaignSender,
  setDraftPaymentRecoverySenders,
  setDraftSelectedChannels,
  setDraftVoiceSettings,
  updateDraftPaymentRecoverySenderAt,
} from '../../redux/actions';
import {
  selectDraftCampaignName,
  selectDraftPaymentRecoverySenders,
  selectDraftSelectedChannels,
  selectDraftVoiceSettings,
} from '../../redux/selectors';
import {
  buildSequentialSenders as buildSequentialSendersUtil,
  CampaignForm,
  EMPTY_SENDER,
  readVoiceCallSettings,
} from '../../utils';
import { campaignBuildSchema } from '../../validator';

const EMAIL_SUBJECT_MAX_LENGTH = 256;

/**
 * Multi-channel messages page for payment recovery campaigns.
 *
 * draftPaymentRecoverySenders layout (set by Build page):
 *   [0] = primary sender (the outbound message)
 *   [1..N] = follow-up senders from integration_mappings (channels selected in Build)
 *
 * Each follow-up sender is mapped to fallbackSenders[i-1] in the form
 * so MessageAuthoring can bind via senderPath="fallbackSenders.{i-1}".
 */
interface PaymentRecoveryMessagesProps {
  onBack?: () => void;
  isNba?: boolean;
}

export const PaymentRecoveryMessages = ({ onBack, isNba }: PaymentRecoveryMessagesProps = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const campaignId = useSelector(selectCampaignId);
  const slug = useSelector(selectAccountSlug);
  const draftCampaignName = useSelector(selectDraftCampaignName);
  const draftPaymentRecoverySenders = useSelector(selectDraftPaymentRecoverySenders);
  const draftSelectedChannels = useSelector(selectDraftSelectedChannels);
  const draftVoiceSettings = useSelector(selectDraftVoiceSettings);

  // If senders are already in Redux on mount (e.g. navigating from MessagingNBA),
  // skip the loading screen — the init effect will still hydrate the form.
  const [isInitialized, setIsInitialized] = useState(
    () => !!draftPaymentRecoverySenders && draftPaymentRecoverySenders.length > 0
  );
  const initializedRef = useRef(false);
  const [activeSender, setActiveSender] = useState<'primary' | 'not_delivered_fallback' | number>(
    'primary'
  );

  const { getStatus } = useCampaigns(campaignId);
  const { campaign_senders } = useCampaignSenders(campaignId);
  const { listStatus: brainsListStatus } = useBrains();

  // Follow-up senders = everything after index 0
  const followUpSenders = useMemo(
    () => draftPaymentRecoverySenders?.slice(1) ?? [],
    [draftPaymentRecoverySenders]
  );

  // Only WhatsApp/Email follow-ups render a configurable card below the divider;
  // any other integration type is skipped (returns null in the map). Use this to
  // decide whether the "Additional channels configuration" divider has anything
  // beneath it — otherwise we'd show an empty section header.
  const renderableFollowUpSenders = useMemo(
    () =>
      followUpSenders.filter(
        (sender) =>
          sender.integrationType === IntegrationType.WHATSAPP ||
          sender.integrationType === IntegrationType.EMAIL
      ),
    [followUpSenders]
  );

  // Build initial fallbackSenders array sized to match the follow-ups
  const initialFallbackSenders = useMemo(
    () => (followUpSenders.length > 0 ? followUpSenders.map(() => ({ ...EMPTY_SENDER })) : []),
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Initialize form
  const methods = useForm<CampaignForm>({
    resolver: createYupResolver<CampaignForm>(campaignBuildSchema),
    defaultValues: {
      primarySender: { ...EMPTY_SENDER },
      fallbackSenders: initialFallbackSenders,
      fallbackIfNotDelivered: false,
      fallbackIfNoResponse: false,
    },
  });

  // Email follow-up subjects keyed by their index in draftPaymentRecoverySenders
  const [emailSubjects, setEmailSubjects] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    followUpSenders.forEach((sender, i) => {
      if (sender.integrationType === IntegrationType.EMAIL) {
        initial[i + 1] = sender.subject || '';
      }
    });
    return initial;
  });

  /**
   * Initialize form from Redux draftPaymentRecoverySenders
   */
  useEffect(() => {
    if (initializedRef.current || !draftPaymentRecoverySenders) {
      return;
    }

    // Index 0 → primarySender in form
    const primary = draftPaymentRecoverySenders[0];
    if (primary?.integrationId) {
      methods.setValue('primarySender', {
        integrationId: primary.integrationId,
        integrationType: (primary.integrationType as IntegrationType) || ('' as IntegrationType),
        content: primary.content || '',
        subject: primary.subject || '',
        template: primary.template,
        headerParameters: primary.headerParameters || [],
        bodyParameters: primary.bodyParameters || [],
        footerParameters: primary.footerParameters || [],
        headerMediaParam: primary.headerMediaParam,
      });
      // Trigger validation after DOM updates with field arrays
      requestAnimationFrame(() => {
        (primary.headerParameters || []).forEach((_, index) => {
          methods.trigger(`primarySender.headerParameters.${index}.name` as Path<CampaignForm>);
        });
        (primary.bodyParameters || []).forEach((_, index) => {
          methods.trigger(`primarySender.bodyParameters.${index}.name` as Path<CampaignForm>);
        });
        (primary.footerParameters || []).forEach((_, index) => {
          methods.trigger(`primarySender.footerParameters.${index}.name` as Path<CampaignForm>);
        });
      });
    }

    // Follow-up senders → fallbackSenders[formIndex] in form
    for (let i = 1; i < draftPaymentRecoverySenders.length; i++) {
      const followUp = draftPaymentRecoverySenders[i];
      const formIndex = i - 1;
      if (followUp?.integrationId) {
        methods.setValue(`fallbackSenders.${formIndex}`, {
          integrationId: followUp.integrationId,
          integrationType: (followUp.integrationType as IntegrationType) || ('' as IntegrationType),
          content: followUp.content || '',
          subject: followUp.subject || '',
          template: followUp.template,
          headerParameters: followUp.headerParameters || [],
          bodyParameters: followUp.bodyParameters || [],
          footerParameters: followUp.footerParameters || [],
          headerMediaParam: followUp.headerMediaParam,
        });
      }
    }

    initializedRef.current = true;
    setIsInitialized(true);
  }, [draftPaymentRecoverySenders, methods]);

  /**
   * The opener settings are campaign-level, but the wizard save runs from a
   * different form (useBuildDebtCampaignLogic), so Redux is where the two
   * meet. Seed the local form once: from Redux when the user has already been
   * here, otherwise from the saved senders.
   */
  const voiceSettingsInitRef = useRef(false);
  useEffect(() => {
    if (voiceSettingsInitRef.current) {
      return;
    }
    // Only the copy authored on this campaign; the draft outlives a switch
    if (draftVoiceSettings?.campaignId === campaignId) {
      methods.setValue('voiceSettings', draftVoiceSettings.settings);
      voiceSettingsInitRef.current = true;
      return;
    }
    if (campaign_senders?.length) {
      methods.setValue('voiceSettings', readVoiceCallSettings(campaign_senders));
      voiceSettingsInitRef.current = true;
    }
  }, [campaign_senders, campaignId, draftVoiceSettings, methods]);

  /**
   * Mirror the opener settings back to Redux as they change. This page has no
   * save of its own, and the user can leave it without pressing "back".
   */
  useEffect(
    () =>
      methods.subscribe({
        formState: { values: true },
        callback: ({ values, name }) => {
          if (name?.startsWith('voiceSettings') && campaignId) {
            dispatch(
              setDraftVoiceSettings({
                campaignId,
                // Deep-cloned: subscribe hands over a shallow copy, so the nested object is
                // RHF's own and Redux would freeze it.
                settings: cloneDeep(values.voiceSettings as VoiceCallSettings) ?? {},
              })
            );
          }
        },
      }),
    [methods, dispatch, campaignId]
  );

  // Extract business logic from shared hook
  const {
    allIntegrations,
    loadingIntegrations,
    whatsappTemplatesMap,
    getTemplatesForIntegration,
    loadingTemplates,
    primarySender,
    fallbackSenders,
    onPrimarySenderChange,
    onFallbackSenderChange,
    onNoResponseSenderChange,
  } = useCampaignBuildLogic(methods);

  const { setValue, formState } = methods;

  // Watch primary sender parameter arrays for unfilled-variables check
  const watchedParams = useWatch({
    control: methods.control,
    name: [
      'primarySender.headerParameters',
      'primarySender.bodyParameters',
      'primarySender.footerParameters',
    ],
  });

  const hasUnfilledVariables = useMemo(() => {
    if (primarySender?.integrationType !== IntegrationType.WHATSAPP) {
      return false;
    }
    const [headerParams, bodyParams, footerParams] = watchedParams;
    const isParamEmpty = (param: { name: string | Record<string, unknown> }) => {
      if (!param?.name) {
        return true;
      }
      return typeof param.name === 'string' && param.name.trim() === '';
    };
    return (
      (headerParams || []).some(isParamEmpty) ||
      (bodyParams || []).some(isParamEmpty) ||
      (footerParams || []).some(isParamEmpty)
    );
  }, [primarySender?.integrationType, watchedParams]);

  const variableErrorMessage = hasUnfilledVariables
    ? t('campaigns.build.all_variables_required')
    : undefined;

  /**
   * Check for variable errors on a specific sender path (same pattern as CampaignBuildForm)
   */
  const getVariableErrorMessage = (senderPath: string) => {
    const { errors } = formState;
    const fields = [
      `${senderPath}.headerParameters`,
      `${senderPath}.bodyParameters`,
      `${senderPath}.footerParameters`,
    ];

    const hasErrors = fields.some((field) => !!get(errors, field));

    return hasErrors ? t('campaigns.build.all_variables_required') : null;
  };

  /**
   * Sync all form data back to Redux before navigating away.
   * Iterates over all senders dynamically.
   */
  const syncToRedux = useCallback(() => {
    if (!draftPaymentRecoverySenders) {
      return;
    }

    const formData = methods.getValues();
    const updated: DraftCampaignSender[] = [...draftPaymentRecoverySenders];

    // Primary sender (index 0) from form's primarySender
    updated[0] = {
      ...updated[0],
      integrationId: formData.primarySender.integrationId,
      integrationType: formData.primarySender.integrationType,
      content: formData.primarySender.content,
      subject: formData.primarySender.subject,
      template: formData.primarySender.template,
      headerParameters: formData.primarySender
        .headerParameters as DraftCampaignSender['headerParameters'],
      bodyParameters: formData.primarySender
        .bodyParameters as DraftCampaignSender['bodyParameters'],
      footerParameters: formData.primarySender
        .footerParameters as DraftCampaignSender['footerParameters'],
      headerMediaParam: formData.primarySender
        .headerMediaParam as DraftCampaignSender['headerMediaParam'],
    };

    // Follow-up senders (index 1+)
    for (let i = 1; i < updated.length; i++) {
      const formIndex = i - 1;
      const followUp = updated[i];
      const formSender = formData.fallbackSenders?.[formIndex];

      if (followUp.integrationType === IntegrationType.WHATSAPP && formSender) {
        // WA follow-up: sync template + parameters from form
        updated[i] = {
          ...followUp,
          template: formSender.template,
          headerParameters: formSender.headerParameters as DraftCampaignSender['headerParameters'],
          bodyParameters: formSender.bodyParameters as DraftCampaignSender['bodyParameters'],
          footerParameters: formSender.footerParameters as DraftCampaignSender['footerParameters'],
          headerMediaParam: formSender.headerMediaParam as DraftCampaignSender['headerMediaParam'],
        };
      } else if (followUp.integrationType === IntegrationType.EMAIL) {
        // Email follow-up: sync subject from local state
        updated[i] = { ...followUp, subject: emailSubjects[i] || '' };
      } else if (formSender) {
        // Voice, SMS, RCS follow-up: sync content from form
        updated[i] = { ...followUp, content: formSender.content || '' };
      }
    }

    dispatch(setDraftPaymentRecoverySenders(updated));
  }, [dispatch, methods, draftPaymentRecoverySenders, emailSubjects]);

  const handleBackToBuild = useCallback(() => {
    syncToRedux();
    if (onBack) {
      onBack();
    } else {
      navigate(`/${slug}/campaigns/${campaignId}/payment-recovery/build`);
    }
  }, [syncToRedux, onBack, navigate, slug, campaignId]);

  // Loading state
  const isLoadingEssentialData =
    getStatus === 'pending' || brainsListStatus === 'pending' || !isInitialized;

  if (isLoadingEssentialData) {
    return (
      <>
        <PageSubHeader breadcrumbs={<CampaignBreadcrumbs campaignName={draftCampaignName} />} />
        <PageContentWrapper>
          <CampaignWrapper>
            <div className="mb-4 flex items-end justify-between px-2">
              <div className="flex flex-col gap-2">
                {isNba ? (
                  <h2 className="text-xl leading-none font-semibold">
                    {t('campaigns.messaging.configuration_title')}
                  </h2>
                ) : (
                  <>
                    <p className="text-sm text-foreground-muted">
                      {t('campaigns.builddebtcampaign.title')}
                    </p>
                    <h2 className="text-xl leading-none font-semibold">
                      {t('campaigns.messages.title')}
                    </h2>
                  </>
                )}
              </div>
              <Button variant="primary" disabled>
                {!isNba && <ArrowLeftIcon className="size-4" />}
                {t('campaigns.messages.back_to_setup')}
              </Button>
            </div>
            <BuildPageLoadingState />
          </CampaignWrapper>
        </PageContentWrapper>
      </>
    );
  }

  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <CampaignBreadcrumbs
            campaignName={draftCampaignName}
            currentStep={t('campaigns.messages.title')}
          />
        }
      />
      <PageContentWrapper>
        <CampaignWrapper>
          {/* Header */}
          <div className="mb-4 flex items-end justify-between px-2">
            <div className="flex flex-col gap-2">
              {isNba ? (
                <h2 className="text-xl leading-none font-semibold">
                  {t('campaigns.messaging.configuration_title')}
                </h2>
              ) : (
                <>
                  <p className="text-sm text-foreground-muted">
                    {t('campaigns.builddebtcampaign.title')}
                  </p>
                  <h2 className="text-xl leading-none font-semibold">
                    {t('campaigns.messages.title')}
                  </h2>
                </>
              )}
            </div>
            <Button variant="primary" onClick={handleBackToBuild}>
              {!isNba && <ArrowLeftIcon className="size-4" />}
              {t('campaigns.messages.back_to_setup')}
            </Button>
          </div>

          {/* Two-column layout like CampaignBuildForm */}
          <FormProvider {...methods}>
            <Form {...methods}>
              <div className="flex w-full max-w-5xl flex-col gap-4 rounded-xl bg-bg-muted p-1 lg:flex-row">
                {/* Left Panel - Scrollable cards */}
                <div className="w-full pb-20 lg:w-[500px] lg:overflow-y-auto lg:pr-2">
                  <form onSubmit={(e) => e.preventDefault()}>
                    {/* ── Primary Message Card ── */}
                    <Card className="mb-4 bg-background pb-7 text-foreground shadow-none">
                      <CardContent className="space-y-6">
                        {isNba && primarySender?.integrationType ? (
                          <div className="flex items-center gap-2">
                            <IntegrationIcon
                              type={primarySender.integrationType as IntegrationType}
                              className="size-5"
                            />
                            <h2 className="text-base font-semibold">
                              {getChannelDisplayName(primarySender.integrationType)}
                            </h2>
                          </div>
                        ) : (
                          <h2 className="text-base font-semibold">
                            {t('campaigns.messages.primary_message')}
                          </h2>
                        )}

                        <FormField
                          control={methods.control}
                          name="primarySender.integrationId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                {t('campaigns.build.send_from')}
                              </FormLabel>
                              <IntegrationSelectContent
                                integrations={allIntegrations}
                                loading={loadingIntegrations}
                                value={field.value || ''}
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  onPrimarySenderChange(value);
                                  const integration = allIntegrations.find(
                                    (i) => i.integration_id === value
                                  );

                                  if (isNba && draftSelectedChannels) {
                                    // Update draftSelectedChannels[0] and rebuild senders
                                    const newChannels = [...draftSelectedChannels];
                                    newChannels[0] = value;
                                    dispatch(setDraftSelectedChannels(newChannels));

                                    const rebuilt = buildSequentialSendersUtil(
                                      newChannels,
                                      allIntegrations,
                                      draftPaymentRecoverySenders ?? undefined
                                    );
                                    dispatch(setDraftPaymentRecoverySenders(rebuilt));

                                    // Re-initialize form from rebuilt senders
                                    const newPrimary = rebuilt[0];
                                    if (newPrimary) {
                                      methods.setValue('primarySender', {
                                        integrationId: newPrimary.integrationId,
                                        integrationType:
                                          (newPrimary.integrationType as IntegrationType) ||
                                          ('' as IntegrationType),
                                        content: newPrimary.content || '',
                                        subject: newPrimary.subject || '',
                                        template: newPrimary.template,
                                        headerParameters: newPrimary.headerParameters || [],
                                        bodyParameters: newPrimary.bodyParameters || [],
                                        footerParameters: newPrimary.footerParameters || [],
                                        headerMediaParam: newPrimary.headerMediaParam,
                                      });
                                    }
                                    // Re-init follow-up form fields
                                    for (let i = 1; i < rebuilt.length; i++) {
                                      const followUp = rebuilt[i];
                                      methods.setValue(`fallbackSenders.${i - 1}`, {
                                        integrationId: followUp.integrationId,
                                        integrationType:
                                          (followUp.integrationType as IntegrationType) ||
                                          ('' as IntegrationType),
                                        content: followUp.content || '',
                                        subject: followUp.subject || '',
                                        template: followUp.template,
                                        headerParameters: followUp.headerParameters || [],
                                        bodyParameters: followUp.bodyParameters || [],
                                        footerParameters: followUp.footerParameters || [],
                                        headerMediaParam: followUp.headerMediaParam,
                                      });
                                    }
                                    // Update email subjects state
                                    const newEmailSubjects: Record<number, string> = {};
                                    rebuilt.forEach((s, idx) => {
                                      if (idx > 0 && s.integrationType === IntegrationType.EMAIL) {
                                        newEmailSubjects[idx] = s.subject || '';
                                      }
                                    });
                                    setEmailSubjects(newEmailSubjects);
                                  } else {
                                    dispatch(
                                      updateDraftPaymentRecoverySenderAt({
                                        index: 0,
                                        data: {
                                          integrationId: value,
                                          integrationType: integration?.type || '',
                                          content: '',
                                          subject: '',
                                          template: undefined,
                                        },
                                      })
                                    );
                                  }
                                }}
                                onOpenChange={(open) => {
                                  if (open) {
                                    setActiveSender('primary');
                                  }
                                }}
                              />
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {primarySender?.integrationType && (
                          <div
                            onClick={() => setActiveSender('primary')}
                            onKeyDown={(e) => {
                              if (
                                (e.key === 'Enter' || e.key === ' ') &&
                                e.target === e.currentTarget
                              ) {
                                e.preventDefault();
                                setActiveSender('primary');
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={t('campaigns.messages.primary_message')}
                          >
                            <MessageAuthoring
                              integrationType={primarySender.integrationType}
                              content={primarySender.content}
                              templateId={primarySender?.template?.id}
                              templates={getTemplatesForIntegration(primarySender.integrationId)}
                              loadingTemplates={loadingTemplates}
                              onContentChange={(content) => {
                                setValue('primarySender.content', content);
                                dispatch(
                                  updateDraftPaymentRecoverySenderAt({
                                    index: 0,
                                    data: { content },
                                  })
                                );
                              }}
                              onTemplateChange={(template) => {
                                setValue('primarySender.template', template);
                                dispatch(
                                  updateDraftPaymentRecoverySenderAt({
                                    index: 0,
                                    data: { template },
                                  })
                                );
                              }}
                              variableErrorMessage={
                                variableErrorMessage || getVariableErrorMessage('primarySender')
                              }
                              senderPath="primarySender"
                              onFocus={() => setActiveSender('primary')}
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* ── Section divider for NBA ── */}
                    {isNba && renderableFollowUpSenders.length > 0 && (
                      <div className="flex items-center gap-4 py-2">
                        <Separator className="flex-1" />
                        <span className="text-sm text-foreground-muted">
                          {t('campaigns.messaging.additional_channels')}
                        </span>
                        <Separator className="flex-1" />
                      </div>
                    )}

                    {/* ── Follow-up Sender Cards (driven by integration_mappings) ── */}
                    {followUpSenders.map((followUpSender, arrayIndex) => {
                      // arrayIndex is 0-based within followUpSenders
                      // reduxIndex is the index in draftPaymentRecoverySenders (1-based)
                      const reduxIndex = arrayIndex + 1;
                      // formIndex maps to fallbackSenders[arrayIndex] in the form
                      const senderPath =
                        `fallbackSenders.${arrayIndex}` as `fallbackSenders.${number}`;
                      const isWA = followUpSender.integrationType === IntegrationType.WHATSAPP;
                      const isEmail = followUpSender.integrationType === IntegrationType.EMAIL;

                      if (isWA) {
                        return (
                          <Card
                            key={followUpSender.integrationId || reduxIndex}
                            className="mb-4 pb-7 shadow-none"
                          >
                            <CardContent className="space-y-6">
                              {isNba ? (
                                <div className="flex items-center gap-2">
                                  <IntegrationIcon
                                    type={IntegrationType.WHATSAPP}
                                    className="size-5"
                                  />
                                  <h2 className="text-base font-semibold">WhatsApp</h2>
                                </div>
                              ) : (
                                <div>
                                  <h2 className="text-base font-semibold">
                                    {t('campaigns.messages.whatsapp_followup')}
                                  </h2>
                                  <p className="mt-1 text-sm text-foreground-muted">
                                    {t('campaigns.messages.whatsapp_followup_description')}
                                  </p>
                                </div>
                              )}

                              {isNba ? (
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-medium">
                                    {t('campaigns.build.send_from')}
                                  </Label>
                                  <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                                    <IntegrationIcon
                                      type={followUpSender.integrationType as IntegrationType}
                                      className="size-4"
                                    />
                                    <span className="text-sm">
                                      {allIntegrations.find(
                                        (i) => i.integration_id === followUpSender.integrationId
                                      )?.name || followUpSender.integrationType}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <FormField
                                  control={methods.control}
                                  name={`fallbackSenders.${arrayIndex}.integrationId`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="text-sm font-medium">
                                        {t('campaigns.build.send_from')}
                                      </FormLabel>
                                      <IntegrationSelectContent
                                        integrations={allIntegrations}
                                        loading={loadingIntegrations}
                                        value={field.value || ''}
                                        onValueChange={(value) => {
                                          field.onChange(value);
                                          if (arrayIndex === 0) {
                                            onFallbackSenderChange(value);
                                          } else {
                                            onNoResponseSenderChange(value, arrayIndex);
                                          }
                                          const integration = allIntegrations.find(
                                            (i) => i.integration_id === value
                                          );
                                          dispatch(
                                            updateDraftPaymentRecoverySenderAt({
                                              index: reduxIndex,
                                              data: {
                                                integrationId: value,
                                                integrationType: integration?.type || '',
                                                content: '',
                                                subject: '',
                                                template: undefined,
                                              },
                                            })
                                          );
                                        }}
                                        onOpenChange={(open) => {
                                          if (open) {
                                            setActiveSender(
                                              arrayIndex === 0
                                                ? 'not_delivered_fallback'
                                                : arrayIndex + 1
                                            );
                                          }
                                        }}
                                      />
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              )}

                              {fallbackSenders?.[arrayIndex]?.integrationType && (
                                <div
                                  onClick={() =>
                                    setActiveSender(
                                      arrayIndex === 0 ? 'not_delivered_fallback' : arrayIndex + 1
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (
                                      (e.key === 'Enter' || e.key === ' ') &&
                                      e.target === e.currentTarget
                                    ) {
                                      e.preventDefault();
                                      setActiveSender(
                                        arrayIndex === 0 ? 'not_delivered_fallback' : arrayIndex + 1
                                      );
                                    }
                                  }}
                                  tabIndex={0}
                                  role="button"
                                  aria-label={t('campaigns.messages.whatsapp_followup')}
                                >
                                  <MessageAuthoring
                                    integrationType={fallbackSenders[arrayIndex].integrationType}
                                    content={fallbackSenders[arrayIndex].content}
                                    templateId={fallbackSenders[arrayIndex]?.template?.id}
                                    templates={getTemplatesForIntegration(
                                      fallbackSenders[arrayIndex]?.integrationId
                                    )}
                                    loadingTemplates={loadingTemplates}
                                    onContentChange={(content) =>
                                      setValue(`fallbackSenders.${arrayIndex}.content`, content)
                                    }
                                    onTemplateChange={(template) =>
                                      setValue(`fallbackSenders.${arrayIndex}.template`, template)
                                    }
                                    variableErrorMessage={getVariableErrorMessage(senderPath)}
                                    senderPath={senderPath}
                                    onFocus={() =>
                                      setActiveSender(
                                        arrayIndex === 0 ? 'not_delivered_fallback' : arrayIndex + 1
                                      )
                                    }
                                  />
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      }

                      if (isEmail) {
                        return (
                          <Card
                            key={followUpSender.integrationId || reduxIndex}
                            className="mb-4 pb-7 shadow-none"
                          >
                            <CardContent className="space-y-6">
                              {isNba ? (
                                <div className="flex items-center gap-2">
                                  <IntegrationIcon
                                    type={IntegrationType.EMAIL}
                                    className="size-5"
                                  />
                                  <h2 className="text-base font-semibold">Email</h2>
                                </div>
                              ) : (
                                <div>
                                  <h2 className="text-base font-semibold">
                                    {t('campaigns.messages.email_followup')}
                                  </h2>
                                  <p className="mt-1 text-sm text-foreground-muted">
                                    {t('campaigns.messages.email_followup_description')}
                                  </p>
                                </div>
                              )}

                              <div className="space-y-2">
                                <Label className="text-sm font-medium">
                                  {t('campaigns.messages.email_followup_subject')}
                                </Label>
                                <Input
                                  value={emailSubjects[reduxIndex] || ''}
                                  onChange={(e) => {
                                    const value = e.target.value.slice(0, EMAIL_SUBJECT_MAX_LENGTH);
                                    setEmailSubjects((prev) => ({ ...prev, [reduxIndex]: value }));
                                  }}
                                  maxLength={EMAIL_SUBJECT_MAX_LENGTH}
                                  placeholder={t('campaigns.messages.email_followup_subject')}
                                />
                                <p className="text-xs text-foreground-muted">
                                  {(emailSubjects[reduxIndex] || '').length}/
                                  {EMAIL_SUBJECT_MAX_LENGTH}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      }

                      // Unsupported integration type — skip
                      return null;
                    })}
                  </form>
                </div>

                {/* Right Panel - Sticky Preview */}
                <div className="w-full flex-1 lg:sticky lg:top-0 lg:h-fit lg:w-auto">
                  <MessagePreview
                    integrations={allIntegrations}
                    whatsappTemplatesMap={whatsappTemplatesMap}
                    activeSender={activeSender}
                    primarySender={primarySender}
                    fallbackSender={fallbackSenders?.[0]}
                    noResponseSenders={fallbackSenders?.slice(1)}
                  />
                </div>
              </div>
            </Form>
          </FormProvider>
        </CampaignWrapper>
      </PageContentWrapper>
    </>
  );
};
